import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, MapPin, Clock, Smartphone, Laptop, Briefcase, Mail, Zap, Calendar, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";

const BUSINESS_PHONE = "05 49 05 74 90";
const BUSINESS_PHONE_LINK = "tel:+33549057490";

const navLinks = [
  { href: "/reparation-telephone", label: "Téléphone & Tablette", icon: Smartphone },
  { href: "/reparation-ordinateur", label: "Ordinateur", icon: Laptop },
  { href: "/services-professionnels", label: "Professionnels", icon: Briefcase },
  { href: "/click-collect", label: "Click & Collect", icon: Package },
  { href: "/rendez-vous", label: "Rendez-vous", icon: Calendar },
  { href: "/contact", label: "Contact", icon: Mail },
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      {/* Top bar */}
      <div className="hidden md:block bg-slate-900 text-white text-sm">
        <div className="container-custom flex items-center justify-between py-2">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-400" />
              104 Rue de la Gare, 79000 Niort
            </span>
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-400" />
              Lun-Ven: 10h-18h | Sam: 10h-16h
            </span>
          </div>
          <a 
            href={BUSINESS_PHONE_LINK} 
            className="flex items-center gap-2 font-semibold hover:text-blue-400 transition-colors"
            data-testid="header-phone-link"
          >
            <Phone className="h-4 w-4" />
            {BUSINESS_PHONE}
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container-custom">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_2010016e-c6ae-4f83-80db-30e5f9c887c1/artifacts/7vmwauk8_LOGO%20MEDIASTORE.JPG" 
              alt="Logo Mediastore - Spécialiste Apple à Niort" 
              className="h-12 w-12 rounded-full object-cover shadow-md"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-xl font-extrabold text-red-600 tracking-tight">Mediastore</span>
              <span className="text-xs font-medium text-slate-500 mt-0.5">Apple Certified · Niort</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? "bg-blue-50 text-blue-600"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/urgence">
              <Button 
                variant="outline" 
                className="border-red-500 text-red-500 hover:bg-red-50 gap-2"
                data-testid="header-emergency-btn"
              >
                <Zap className="h-4 w-4" />
                Urgence
              </Button>
            </Link>
            <a href={BUSINESS_PHONE_LINK}>
              <Button 
                className="bg-red-600 hover:bg-red-700 gap-2"
                data-testid="header-call-btn"
              >
                <Phone className="h-4 w-4" />
                Appeler
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-bold text-xl">Menu</span>
                </div>
                
                <nav className="flex flex-col gap-2">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <SheetClose asChild key={link.href}>
                        <Link
                          to={link.href}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                            isActive(link.href)
                              ? "bg-blue-50 text-blue-600"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                          data-testid={`mobile-nav-${link.href.slice(1)}`}
                        >
                          <Icon className="h-5 w-5" />
                          {link.label}
                        </Link>
                      </SheetClose>
                    );
                  })}
                </nav>

                <div className="mt-auto pt-8 space-y-3">
                  <SheetClose asChild>
                    <Link to="/urgence" className="block">
                      <Button 
                        variant="outline" 
                        className="w-full border-orange-500 text-orange-500 hover:bg-orange-50 gap-2"
                        data-testid="mobile-emergency-btn"
                      >
                        <Zap className="h-4 w-4" />
                        Réparation Urgente
                      </Button>
                    </Link>
                  </SheetClose>
                  
                  <a href={BUSINESS_PHONE_LINK} className="block">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
                      data-testid="mobile-call-btn"
                    >
                      <Phone className="h-4 w-4" />
                      Appeler maintenant
                    </Button>
                  </a>

                  <div className="text-center text-sm text-slate-500 pt-4">
                    <p className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4" />
                      104 Rue de la Gare, Niort
                    </p>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
